import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { authService } from "./userService";
import { toast } from "react-toastify";

export const registerUser = createAsyncThunk(
  "auth/register",
  async (userData, thunkAPI) => {
    try {
      const response = await authService.registerWithOrderTransfer(userData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/login",
  async (userData, thunkAPI) => {
    try {
      return await authService.login(userData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const googleLoginUser = createAsyncThunk(
  "auth/google-login",
  async (userData, thunkAPI) => {
    try {
      return await authService.googleLogin(userData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getuserProductWishlist = createAsyncThunk(
  "user/wishlist",
  async (_, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.getUserWishlist(config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addProdToCart = createAsyncThunk(
  "user/cart/add",
  async (cartData, thunkAPI) => {
    try {
      // Check if user is logged in
      const customer = localStorage.getItem("customer");
      if (customer) {
        // Create dynamic config with current token
        const getTokenFromLocalStorage = JSON.parse(customer);
        const config = {
          headers: {
            Authorization: `Bearer ${
              getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
            }`,
            Accept: "application/json",
          },
        };
        return await authService.addToCart(cartData, config);
      } else {
        // Use guest cart functionality
        return await authService.addToCartGuest(cartData);
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getUserCart = createAsyncThunk(
  "user/cart/get",
  async (_, thunkAPI) => {
    try {
      // Check if user is logged in
      const customer = localStorage.getItem("customer");
      if (customer) {
        // Create dynamic config with current token
        const getTokenFromLocalStorage = JSON.parse(customer);
        const config = {
          headers: {
            Authorization: `Bearer ${
              getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
            }`,
            Accept: "application/json",
          },
        };
        return await authService.getCart(config);
      } else {
        // Use guest cart functionality
        return await authService.getGuestCart();
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteUserCart = createAsyncThunk(
  "user/cart/delete",
  async (_, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.emptyCart(config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getOrders = createAsyncThunk(
  "user/orders/get",
  async (_, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.getUserOrders(config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getSingleOrder = createAsyncThunk(
  "user/order/get",
  async (orderId, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.getSingleUserOrder(orderId, config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteCartProduct = createAsyncThunk(
  "user/cart/product/delete",
  async (data, thunkAPI) => {
    try {
      // Check if user is logged in
      const customer = localStorage.getItem("customer");
      if (customer) {
        // Create dynamic config with current token
        const getTokenFromLocalStorage = JSON.parse(customer);
        const config = {
          headers: {
            Authorization: `Bearer ${
              getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
            }`,
            Accept: "application/json",
          },
        };
        return await authService.removeProductFromCart({ ...data, config });
      } else {
        // Use guest cart functionality
        return await authService.removeProductFromGuestCart(data.id);
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createAnOrder = createAsyncThunk(
  "user/cart/create-order",
  async (orderDetail, thunkAPI) => {
    try {
      // Check if user is logged in
      const customer = localStorage.getItem("customer");
      if (customer) {
        // Create dynamic config with current token
        const getTokenFromLocalStorage = JSON.parse(customer);
        const config = {
          headers: {
            Authorization: `Bearer ${
              getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
            }`,
            Accept: "application/json",
          },
        };
        return await authService.createOrder(orderDetail, config);
      } else {
        // Use guest order functionality
        return await authService.createGuestOrder(orderDetail);
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updatePreorderPayment = createAsyncThunk(
  "user/order/update-preorder-payment",
  async ({ orderId, paymentInfo }, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.updatePreorderPayment(orderId, paymentInfo, config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateCartProduct = createAsyncThunk(
  "user/cart/product/update",
  async (cartDetail, thunkAPI) => {
    try {
      // Check if user is logged in
      const customer = localStorage.getItem("customer");
      if (customer) {
        // Create dynamic config with current token
        const getTokenFromLocalStorage = JSON.parse(customer);
        const config = {
          headers: {
            Authorization: `Bearer ${
              getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
            }`,
            Accept: "application/json",
          },
        };
        return await authService.updateProductFromCart(cartDetail, config);
      } else {
        // Use guest cart functionality
        return await authService.updateProductQuantityFromGuestCart(
          cartDetail.cartItemId,
          cartDetail.quantity
        );
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateProfile = createAsyncThunk(
  "user/profile/update",
  async (data, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.updateUser({ ...data, config });
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// Address management thunks
export const saveUserAddress = createAsyncThunk(
  "user/address/save",
  async (addressData, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.saveUserAddress(addressData, config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getUserAddresses = createAsyncThunk(
  "user/address/get",
  async (_, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.getUserAddresses(config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateUserAddress = createAsyncThunk(
  "user/address/update",
  async ({ addressId, addressData }, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.updateUserAddress(addressId, addressData, config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteUserAddress = createAsyncThunk(
  "user/address/delete",
  async (addressId, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.deleteUserAddress(addressId, config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const setDefaultAddress = createAsyncThunk(
  "user/address/setDefault",
  async (addressId, thunkAPI) => {
    try {
      // Create dynamic config with current token
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      return await authService.setDefaultAddress(addressId, config);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const forgotPasswordToken = createAsyncThunk(
  "user/password/token",
  async (data, thunkAPI) => {
    try {
      return await authService.forgotPasswordToken(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/password/reset",
  async (data, thunkAPI) => {
    try {
      return await authService.resetPass(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// Guest wishlist functionality
export const addToWishlistGuest = createAsyncThunk(
  "user/wishlist/guest",
  async (prodId, thunkAPI) => {
    try {
      return await authService.addToWishlistGuest(prodId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const resetState = createAction("Reset_all");

export const resendVerificationEmail = createAsyncThunk(
  "auth/resend-verification-email",
  async (userData, thunkAPI) => {
    try {
      return await authService.resendVerificationEmail(userData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const getCustomerfromLocalStorage = localStorage.getItem("customer")
  ? JSON.parse(localStorage.getItem("customer"))
  : null;

const initialState = {
  user: getCustomerfromLocalStorage || [],
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: "",
  wishlist: [],
  cart: [],
  orderedProduct: [],
  getorderedProduct: [],
  singleOrder: null,
  compareProduct: [],
  addresses: [],
  guestCart: [],
  guestWishlist: [],
};

export const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    setGuestCartItems: (state, action) => {
      state.guestCart = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.createdUser = action.payload;
        if (state.isSuccess === true) {
          toast.info("User Created Successfully");
        }
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError === true) {
          // Provide more user-friendly error messages
          const errorMsg = action.payload?.response?.data?.message || 
                          action.payload?.message || 
                          "An error occurred during registration";
          
          // Handle specific error cases
          if (errorMsg.includes("email already exists")) {
            toast.error("An account with this email already exists. Please use a different email.");
          } else if (errorMsg.includes("mobile number already exists")) {
            toast.error("An account with this mobile number already exists. Please use a different mobile number.");
          } else if (errorMsg.includes("User Already Exists")) {
            toast.error("An account with this email or mobile number already exists.");
          } else {
            toast.error(errorMsg);
          }
        }
      })
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.user = action.payload;
        if (state.isSuccess === true) {
          localStorage.setItem("customer", JSON.stringify(action.payload));
          toast.info("User Logged In Successfully");
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError === true) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401) {
            const errorMsg = action.payload.response.data.message;
            if (errorMsg && errorMsg.includes("Please verify your email address")) {
              toast.error(`${errorMsg} You can resend the verification email at the Resend Verification page.`);
            } else {
              toast.error("Invalid credentials. Please try again.");
            }
          } else {
            toast.error(action.payload.response.data.message);
          }
        }
      })
      .addCase(googleLoginUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(googleLoginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.user = action.payload;
        if (state.isSuccess === true) {
          localStorage.setItem("customer", JSON.stringify(action.payload));
          toast.info("User Logged In Successfully with Google");
        }
      })
      .addCase(googleLoginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError === true) {
          toast.error("Google authentication failed. Please try again.");
        }
      })
      .addCase(getuserProductWishlist.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getuserProductWishlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.wishlist = action.payload;
      })
      .addCase(getuserProductWishlist.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        
        // Handle authentication errors
        if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
          // Check if it's a session invalidation error
          if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
            toast.error("Your session has been invalidated. Please log in again.");
          }
          // Check if it's a token expiration error
          else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
            toast.error("Your session has expired. Please log in again.");
          } else {
            // User will be redirected to login by the service layer
            toast.error("Session expired. Please log in again.");
          }
        }
      })
      .addCase(addProdToCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addProdToCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.cartProduct = action.payload;
        if (state.isSuccess) {
          console.log("Product Added To Cart");
        }
      })
      .addCase(addProdToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        
        // Handle authentication errors
        if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
          // Check if it's a session invalidation error
          if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
            toast.error("Your session has been invalidated. Please log in again.");
          }
          // Check if it's a token expiration error
          else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
            toast.error("Your session has expired. Please log in again.");
          } else {
            // User will be redirected to login by the service layer
            toast.error("Session expired. Please log in again.");
          }
        } else {
          toast.error("Failed to add product to cart");
        }
      })
      .addCase(getUserCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        
        // Check if user is logged in
        const customer = localStorage.getItem("customer");
        if (customer) {
          // For authenticated users, set cartProducts
          state.cartProducts = action.payload;
        } else {
          // For guest users, set guestCart
          state.guestCart = action.payload;
        }
      })
      .addCase(getUserCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        
        // Handle authentication errors
        if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
          // Check if it's a session invalidation error
          if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
            toast.error("Your session has been invalidated. Please log in again.");
          }
          // Check if it's a token expiration error
          else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
            toast.error("Your session has expired. Please log in again.");
          } else {
            // User will be redirected to login by the service layer
            toast.error("Session expired. Please log in again.");
          }
        }
      })
      .addCase(deleteCartProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteCartProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.deletedCartProduct = action.payload;
        if (state.isSuccess) {
          toast.success("Product Deleted From Cart Successfully!");
          
          // Check if user is logged in
          const customer = localStorage.getItem("customer");
          if (!customer) {
            // For guest users, refresh the guest cart
            // Since we can't directly update the localStorage item, we'll refetch the cart
            // The component will handle refetching the cart data
          }
        }
      })
      .addCase(deleteCartProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
            // Check if it's a session invalidation error
            if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
              toast.error("Your session has been invalidated. Please log in again.");
            }
            // Check if it's a token expiration error
            else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
              toast.error("Your session has expired. Please log in again.");
            } else {
              // User will be redirected to login by the service layer
              toast.error("Session expired. Please log in again.");
            }
          } else {
            toast.error("Failed to remove product from cart");
          }
        }
      })
      .addCase(updateCartProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateCartProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.updatedCartProduct = action.payload;
        if (state.isSuccess) {
          toast.success("Product Updated Successfully!");
          
          // Check if user is logged in
          const customer = localStorage.getItem("customer");
          if (!customer) {
            // For guest users, refresh the guest cart
            // Since we can't directly update the localStorage item, we'll refetch the cart
            // The component will handle refetching the cart data
          }
        }
      })
      .addCase(updateCartProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
            // Check if it's a session invalidation error
            if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
              toast.error("Your session has been invalidated. Please log in again.");
            }
            // Check if it's a token expiration error
            else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
              toast.error("Your session has expired. Please log in again.");
            } else {
              // User will be redirected to login by the service layer
              toast.error("Session expired. Please log in again.");
            }
          } else {
            toast.error("Failed to update product in cart");
          }
        }
      })
      .addCase(createAnOrder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createAnOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.orderedProduct = action.payload;
        if (state.isSuccess) {
          toast.success("Ordered created Successfully!");
        }
      })
      .addCase(createAnOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
            // Check if it's a session invalidation error
            if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
              toast.error("Your session has been invalidated. Please log in again.");
            }
            // Check if it's a token expiration error
            else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
              toast.error("Your session has expired. Please log in again.");
            } else {
              // User will be redirected to login by the service layer
              toast.error("Session expired. Please log in again.");
            }
          } else {
            toast.error("Failed to create order");
          }
        }
      })
      .addCase(updatePreorderPayment.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePreorderPayment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.updatedOrder = action.payload;
        if (state.isSuccess) {
          toast.success("Preorder payment processed successfully!");
        }
      })
      .addCase(updatePreorderPayment.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
            // Check if it's a session invalidation error
            if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
              toast.error("Your session has been invalidated. Please log in again.");
            }
            // Check if it's a token expiration error
            else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
              toast.error("Your session has expired. Please log in again.");
            } else {
              // User will be redirected to login by the service layer
              toast.error("Session expired. Please log in again.");
            }
          } else {
            toast.error("Failed to update preorder payment");
          }
        }
      })
      .addCase(getOrders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.getorderedProduct = action.payload;
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        
        // Handle authentication errors
        if (action.payload?.response?.status === 401) {
          // Check if it's a session invalidation error
          if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
            toast.error("Your session has been invalidated. Please log in again.");
          }
          // Check if it's a token expiration error
          else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
            toast.error("Your session has expired. Please log in again.");
          } else {
            // User will be redirected to login by the service layer
            toast.error("Session expired. Please log in again.");
          }
        }
      })
      .addCase(getSingleOrder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSingleOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.singleOrder = action.payload;
      })
      .addCase(getSingleOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.updatedUser = action.payload;

        if (state.isSuccess === true) {
          let currentUserData = JSON.parse(localStorage.getItem("customer"));
          let newUserData = {
            _id: currentUserData?._id,
            token: currentUserData.token,
            firstname: action?.payload?.firstname,
            lastname: action?.payload?.lastname,
            email: action?.payload?.email,
            mobile: action?.payload?.mobile,
          };
          localStorage.setItem("customer", JSON.stringify(newUserData));
          state.user = newUserData;
          toast.success("Profile Updated Successfully!");
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
            // Check if it's a session invalidation error
            if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
              toast.error("Your session has been invalidated. Please log in again.");
            }
            // Check if it's a token expiration error
            else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
              toast.error("Your session has expired. Please log in again.");
            } else {
              // User will be redirected to login by the service layer
              toast.error("Session expired. Please log in again.");
            }
          } else {
            toast.error("Failed to update profile");
          }
        }
      })

      .addCase(forgotPasswordToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(forgotPasswordToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.token = action.payload;
        if (state.isSuccess) {
          toast.success("Password reset instructions have been sent to your email. Please check your inbox.");
        }
      })
      .addCase(forgotPasswordToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Check if it's a specific error message from the backend
          const errorMsg = action.payload?.response?.data?.message || 
                          action.payload?.message || 
                          "Something went wrong while sending the password reset email. Please try again later.";
          toast.error(errorMsg);
        }
      })
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.pass = action.payload;
        if (state.isSuccess) {
          toast.success("Password changed successfully! You can now log in with your new password.");
        }
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Check if it's a specific error message from the backend
          const errorMsg = action.payload?.response?.data?.message || 
                          action.payload?.message || 
                          "Something went wrong while resetting your password!";
          toast.error(errorMsg);
        }
      })
      .addCase(deleteUserCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteUserCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.deletedCart = action.payload;
      })
      .addCase(deleteUserCart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        
        // Handle authentication errors
        if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
          // Check if it's a session invalidation error
          if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
            toast.error("Your session has been invalidated. Please log in again.");
          }
          // Check if it's a token expiration error
          else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
            toast.error("Your session has expired. Please log in again.");
          } else {
            // User will be redirected to login by the service layer
            toast.error("Session expired. Please log in again.");
          }
        }
      })
      .addCase(addToWishlistGuest.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addToWishlistGuest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.wishlistGuest = action.payload;
        if (state.isSuccess) {
          toast.success(action.payload.message);
        }
      })
      .addCase(addToWishlistGuest.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          toast.error("Something Went Wrong!");
        }
      })
      // Address management reducers
      .addCase(saveUserAddress.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(saveUserAddress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.user = action.payload;
        // Update localStorage with new user data
        if (state.isSuccess === true) {
          let currentUserData = JSON.parse(localStorage.getItem("customer"));
          let newUserData = {
            ...currentUserData,
            address: action.payload.address,
          };
          localStorage.setItem("customer", JSON.stringify(newUserData));
        }
        toast.success("Address Saved Successfully!");
      })
      .addCase(saveUserAddress.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
            // Check if it's a session invalidation error
            if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
              toast.error("Your session has been invalidated. Please log in again.");
            }
            // Check if it's a token expiration error
            else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
              toast.error("Your session has expired. Please log in again.");
            } else {
              // User will be redirected to login by the service layer
              toast.error("Session expired. Please log in again.");
            }
          } else {
            toast.error("Failed to save address");
          }
        }
      })
      .addCase(getUserAddresses.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserAddresses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.userAddresses = action.payload;
      })
      .addCase(getUserAddresses.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        
        // Handle authentication errors
        if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
          // Check if it's a session invalidation error
          if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
            toast.error("Your session has been invalidated. Please log in again.");
          }
          // Check if it's a token expiration error
          else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
            toast.error("Your session has expired. Please log in again.");
          } else {
            // User will be redirected to login by the service layer
            toast.error("Session expired. Please log in again.");
          }
        }
      })
      .addCase(updateUserAddress.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUserAddress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.user = action.payload;
        // Update localStorage with new user data
        if (state.isSuccess === true) {
          let currentUserData = JSON.parse(localStorage.getItem("customer"));
          let newUserData = {
            ...currentUserData,
            address: action.payload.address,
          };
          localStorage.setItem("customer", JSON.stringify(newUserData));
        }
        toast.success("Address Updated Successfully!");
      })
      .addCase(updateUserAddress.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
            // Check if it's a session invalidation error
            if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
              toast.error("Your session has been invalidated. Please log in again.");
            }
            // Check if it's a token expiration error
            else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
              toast.error("Your session has expired. Please log in again.");
            } else {
              // User will be redirected to login by the service layer
              toast.error("Session expired. Please log in again.");
            }
          } else {
            toast.error("Failed to update address");
          }
        }
      })
      .addCase(deleteUserAddress.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteUserAddress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.user = action.payload;
        // Update localStorage with new user data
        if (state.isSuccess === true) {
          let currentUserData = JSON.parse(localStorage.getItem("customer"));
          let newUserData = {
            ...currentUserData,
            address: action.payload.address,
          };
          localStorage.setItem("customer", JSON.stringify(newUserData));
        }
        toast.success("Address Deleted Successfully!");
      })
      .addCase(deleteUserAddress.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
            // Check if it's a session invalidation error
            if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
              toast.error("Your session has been invalidated. Please log in again.");
            }
            // Check if it's a token expiration error
            else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
              toast.error("Your session has expired. Please log in again.");
            } else {
              // User will be redirected to login by the service layer
              toast.error("Session expired. Please log in again.");
            }
          } else {
            toast.error("Failed to delete address");
          }
        }
      })
      .addCase(setDefaultAddress.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.user = action.payload;
        // Update localStorage with new user data
        if (state.isSuccess === true) {
          let currentUserData = JSON.parse(localStorage.getItem("customer"));
          let newUserData = {
            ...currentUserData,
            address: action.payload.address,
          };
          localStorage.setItem("customer", JSON.stringify(newUserData));
        }
        toast.success("Default Address Set Successfully!");
      })
      .addCase(setDefaultAddress.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          // Handle authentication errors
          if (action.payload?.response?.status === 401 || action.payload?.response?.status === 500) {
            // Check if it's a session invalidation error
            if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Session invalidated")) {
              toast.error("Your session has been invalidated. Please log in again.");
            }
            // Check if it's a token expiration error
            else if (action.payload?.response?.data?.message && action.payload?.response?.data?.message.includes("Access token expired")) {
              toast.error("Your session has expired. Please log in again.");
            } else {
              // User will be redirected to login by the service layer
              toast.error("Session expired. Please log in again.");
            }
          } else {
            toast.error("Failed to set default address");
          }
        }
      })
      .addCase(resendVerificationEmail.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resendVerificationEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.verificationEmail = action.payload;
        if (state.isSuccess) {
          toast.success("Verification email sent successfully! Please check your inbox.");
        }
      })
      .addCase(resendVerificationEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
        if (state.isError) {
          const errorMsg = action.payload?.response?.data?.message || "Failed to send verification email. Please try again later.";
          toast.error(errorMsg);
        }
      })
      .addCase(resetState, () => initialState);
  },
});

export default authSlice.reducer;